/**
 * Het scherm van de leerling: de stappen van een les, achter elkaar.
 *
 *   welkom → groepje → personas → chat → argumenten → afronden → klaar
 *
 * Dit bestand is de stappenmachine en houdt vast wat meer dan één stap
 * nodig heeft: de sessie, de discussie, het groepje, het gesprek, de
 * tijdklok, de argumenten en de hulp van Socrates. Elke stap staat in
 * stappen/, de bouwstenen in onderdelen/, en wat er achter de schermen
 * gebeurt in hooks/ en api/leerling.js.
 */
import React, { useEffect, useState } from 'react';
import { LogOut } from 'lucide-react';
import { kleur } from '../../theme/tokens';
import { maakApi } from '../../api/client';
import { maakLeerlingApi } from '../../api/leerling';
import { basis } from './stijlen';
import { useGesprek } from './hooks/useGesprek';
import { useTijdklok } from './hooks/useTijdklok';
import { useHulp } from './hooks/useHulp';
import { Welkom } from './stappen/Welkom';
import { Groepje } from './stappen/Groepje';
import { KiesGesprekspartners } from './stappen/KiesGesprekspartners';
import { Chat } from './stappen/Chat';
import { Argumenten } from './stappen/Argumenten';
import { Afronden, Klaar, GeenSessie, Fout } from './stappen/Afronden';

function LeerlingFlow({ user, token, onLogout, apiUrl }) {
  const [step, setStep] = useState('loading');
  const [fout, setFout] = useState('');
  const [session, setSession] = useState(null);
  const [discussion, setDiscussion] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [discussieleider, setDiscussieleider] = useState(null);
  const [groepje, setGroepje] = useState({ naam: '', avatar: '🦊', genoten: [] });
  const [argumenten, setArgumenten] = useState({ voor: [''], tegen: [''] });

  const { verzoek } = maakApi({ apiUrl, token, bijVerlopenToken: onLogout });
  const api = maakLeerlingApi(verzoek);

  const gesprek = useGesprek({ api, discussion, discussieleider });
  const hulp = useHulp({ api, discussion, argumenten });
  const klok = useTijdklok({
    afrondingMinuten: session?.afronding_duur_min,
    // Na de chattijd praten de leerlingen alleen nog met Socrates.
    bijReflectie: () => gesprek.setActivePersona(discussieleider),
    bijEinde: () => setStep('argumenten'),
  });

  const haalSessie = async () => {
    try {
      const data = await api.haalSessie(user.id);
      if (data.success && data.data) { setSession(data.data); setStep('welkom'); }
      else { setStep('geen-sessie'); }
    } catch (err) { setFout('Kan geen verbinding maken'); setStep('error'); }
  };

  // Eén keer bij het openen; haalSessie is een gewone functie in de
  // component en hoort daarom niet in de lijst.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { haalSessie(); }, []);

  const naGroepje = (nieuweDiscussie) => {
    setDiscussion(nieuweDiscussie);
    setStep('personas');
    // De personages komen binnen terwijl de stap al open is.
    api.haalPersonas(session.session_id)
      .then(data => { if (data.success) setPersonas(data.data); })
      .catch(err => console.error('Fout:', err));
  };

  const naKeuze = async (gekozenPersonas) => {
    setDiscussion({ ...discussion, personas: gekozenPersonas });
    try {
      const data = await api.haalDiscussieleider();
      if (data.success) setDiscussieleider(data.data);
    } catch (err) { console.error('Fout:', err); }
    setTimeout(() => gesprek.start(), 500);
    setStep('chat');
    if (session.chat_duur_min) klok.start(session.chat_duur_min * 60);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img src="/socrates_logo.png" alt="Socrates" style={{ height: '125px' }} />
        <div style={styles.userInfo}>
          <span>{user.naam}</span>
          <button onClick={onLogout} style={styles.logoutBtn}><LogOut size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Uitloggen</button>
        </div>
      </div>

      <div style={styles.content}>
        {step === 'loading' && <div style={styles.card}><p>Laden...</p></div>}
        {step === 'error' && <Fout melding={fout} onOpnieuw={haalSessie} />}
        {step === 'geen-sessie' && <GeenSessie />}

        {step === 'welkom' && session && <Welkom session={session} onStart={() => setStep('groepje')} />}

        {step === 'groepje' && (
          <Groepje api={api} session={session} user={user} groepje={groepje} onWijzigGroepje={setGroepje}
            onTerug={() => setStep('welkom')} onKlaar={naGroepje} />
        )}

        {step === 'personas' && (
          <KiesGesprekspartners api={api} apiUrl={apiUrl} session={session} discussion={discussion} groepje={groepje}
            personas={personas} onTerug={() => setStep('groepje')} onGekozen={naKeuze} />
        )}

        {step === 'chat' && (
          <Chat apiUrl={apiUrl} session={session} discussion={discussion} groepje={groepje} discussieleider={discussieleider}
            gesprek={gesprek} klok={klok} onNaarArgumenten={() => setStep('argumenten')} />
        )}

        {step === 'argumenten' && (
          <Argumenten api={api} session={session} discussion={discussion} groepje={groepje}
            argumenten={argumenten} onWijzig={setArgumenten} hulp={hulp} berichten={gesprek.berichten} klok={klok}
            onTerug={() => setStep('chat')} onOpgeslagen={() => setStep('afronden')} />
        )}

        {step === 'afronden' && (
          <Afronden api={api} discussion={discussion} argumenten={argumenten}
            onAanpassen={() => setStep('argumenten')} onAfgerond={() => setStep('klaar')} />
        )}

        {step === 'klaar' && <Klaar />}
      </div>
    </div>
  );
}

const styles = {
  ...basis,
  header: { background: 'white', padding: '16px 56px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: kleur.inkt },
  userInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
  content: { padding: '16px 24px', maxWidth: '1000px', margin: '0 auto' },
};

export default LeerlingFlow;
